import * as yup from 'yup';

import {
  FOREIGN_TAX_NUMBER_OTHER_PATTERN,
  TIN_PATTERN,
} from '@/constants/patterns';

import { getTranslatedMessage } from '@/i18n/utils';

import {
  NoTaxNumberReasonCategory,
  NoTaxNumberReasonCode,
  TaxNumberTypeCode,
} from '@/types/api/tax-info';

import {
  FOREIGN_NO_TAX_NUMBER_REASON_CATEGORY_CODES,
  FOREIGN_NO_TAX_NUMBER_REASON_OTHER_MAX_LENGTH,
  NO_TAX_NUMBER_REASON_CODE_OPTIONS,
} from '../ForeignNoTaxNumberReasonSection/constants';
import { shouldShowNoTaxNumberReasonCodeDropdown } from '../ForeignNoTaxNumberReasonSection/utils';
import {
  FOREIGN_TAX_NUMBER_TYPE_CODES,
  FOREIGN_TAX_OTHER_NUMBER_TYPE_MAX_LENGTH,
  MAX_FOREIGN_TAX_RESIDENCIES,
} from './constants';

export const foreignTaxResidencyBlockSchema = yup.object({
  countryCode: yup
    .string()
    .required(
      getTranslatedMessage(
        'taxInfoDrawer.question2.foreignTaxResidency.country.required',
      ),
    ),
  taxNumberTypeCode: yup.mixed<TaxNumberTypeCode>().when('noTaxNumber', {
    is: false,
    then: schema =>
      schema
        .oneOf([...FOREIGN_TAX_NUMBER_TYPE_CODES])
        .required(
          getTranslatedMessage(
            'taxInfoDrawer.question2.foreignTaxResidency.taxNumberType.required',
          ),
        ),
    otherwise: schema => schema.notRequired(),
  }),
  otherTaxNumberType: yup.string().when(['noTaxNumber', 'taxNumberTypeCode'], {
    is: (noTaxNumber: boolean, taxNumberTypeCode?: TaxNumberTypeCode) =>
      !noTaxNumber && taxNumberTypeCode === TaxNumberTypeCode.OTHER,
    then: schema =>
      schema
        .required(
          getTranslatedMessage(
            'taxInfoDrawer.question2.foreignTaxResidency.otherTaxNumberType.required',
          ),
        )
        .max(
          FOREIGN_TAX_OTHER_NUMBER_TYPE_MAX_LENGTH,
          getTranslatedMessage(
            'taxInfoDrawer.question2.foreignTaxResidency.otherTaxNumberType.maxLength',
            { count: FOREIGN_TAX_OTHER_NUMBER_TYPE_MAX_LENGTH },
          ),
        ),
    otherwise: schema => schema.notRequired(),
  }),
  taxNumberValue: yup.string().when(['noTaxNumber', 'taxNumberTypeCode'], {
    is: (noTaxNumber: boolean) => noTaxNumber === false,
    then: schema =>
      schema
        .required(
          getTranslatedMessage(
            'taxInfoDrawer.question2.foreignTaxResidency.taxNumber.required',
          ),
        )
        .test(
          'foreign-tax-number-pattern',
          getTranslatedMessage(
            'taxInfoDrawer.question2.foreignTaxResidency.taxNumber.error.pattern',
          ),
          function validateForeignTaxNumber(value) {
            if (!value) {
              return true;
            }

            const taxNumberTypeCode = this.parent.taxNumberTypeCode as
              | TaxNumberTypeCode
              | '';

            if (taxNumberTypeCode === TaxNumberTypeCode.OTHER) {
              const isAlphanumeric = new RegExp(
                FOREIGN_TAX_NUMBER_OTHER_PATTERN,
              ).test(value);

              return (
                isAlphanumeric ||
                this.createError({
                  message: getTranslatedMessage(
                    'taxInfoDrawer.question2.foreignTaxResidency.taxNumber.error.alphanumeric',
                  ),
                })
              );
            }

            return (
              new RegExp(TIN_PATTERN).test(value) ||
              this.createError({
                message: getTranslatedMessage(
                  'taxInfoDrawer.question2.foreignTaxResidency.taxNumber.error.pattern',
                ),
              })
            );
          },
        ),
    otherwise: schema => schema.notRequired(),
  }),
  noTaxNumber: yup.boolean().default(false),
  noTaxNumberReasonCategory: yup.mixed().when('noTaxNumber', {
    is: true,
    then: schema =>
      schema
        .oneOf([...FOREIGN_NO_TAX_NUMBER_REASON_CATEGORY_CODES])
        .required(
          getTranslatedMessage(
            'taxInfoDrawer.question2.foreignTaxResidency.noTaxNumberReason.required',
          ),
        ),
    otherwise: schema => schema.notRequired(),
  }),
  noTaxNumberReasonCode: yup
    .string()
    .when(['noTaxNumber', 'noTaxNumberReasonCategory'], {
      is: (noTaxNumber: boolean, noTaxNumberReasonCategory?: string) =>
        noTaxNumber &&
        shouldShowNoTaxNumberReasonCodeDropdown(
          noTaxNumberReasonCategory as NoTaxNumberReasonCategory | '',
        ),
      then: schema =>
        schema
          .oneOf([...NO_TAX_NUMBER_REASON_CODE_OPTIONS])
          .required(
            getTranslatedMessage(
              'taxInfoDrawer.question2.foreignTaxResidency.noTaxNumberReason.codeRequired',
            ),
          ),
      otherwise: schema => schema.notRequired(),
    }),
  noTaxNumberReasonOther: yup
    .string()
    .when(['noTaxNumber', 'noTaxNumberReasonCode'], {
      is: (noTaxNumber: boolean, noTaxNumberReasonCode?: string) =>
        noTaxNumber &&
        noTaxNumberReasonCode === NoTaxNumberReasonCode.OTHER_UNABLE_TO_PROVIDE,
      then: schema =>
        schema
          .required(
            getTranslatedMessage(
              'taxInfoDrawer.question2.foreignTaxResidency.noTaxNumberReason.other.required',
            ),
          )
          .max(
            FOREIGN_NO_TAX_NUMBER_REASON_OTHER_MAX_LENGTH,
            getTranslatedMessage(
              'taxInfoDrawer.question2.foreignTaxResidency.noTaxNumberReason.other.maxLength',
              { count: FOREIGN_NO_TAX_NUMBER_REASON_OTHER_MAX_LENGTH },
            ),
          ),
      otherwise: schema => schema.notRequired(),
    }),
});

export function buildForeignTaxResidenciesValidator() {
  return yup
    .array()
    .default([])
    .when(['hasForeignIndicia', 'isForeignTaxResident'], {
      is: (hasForeignIndicia: boolean, isForeignTaxResident: boolean) =>
        typeof hasForeignIndicia === 'boolean' && isForeignTaxResident === true,
      then: schema =>
        schema
          .of(foreignTaxResidencyBlockSchema)
          .min(
            1,
            getTranslatedMessage(
              'taxInfoDrawer.question2.foreignTaxResidency.required',
            ),
          )
          .max(MAX_FOREIGN_TAX_RESIDENCIES)
          .test(
            'unique-foreign-tax-countries',
            getTranslatedMessage(
              'taxInfoDrawer.question2.foreignTaxResidency.country.duplicate',
            ),
            value => {
              const countryCodes = (value ?? [])
                .map(block => block?.countryCode)
                .filter(Boolean);

              return new Set(countryCodes).size === countryCodes.length;
            },
          ),
      otherwise: schema => schema.notRequired(),
    });
}
